import { useEffect } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import { KeyDocumentsSection } from './components/documents-overview';
import { KeyEntitiesSection } from './components/entities-overview';
import { IssuesOverview } from './components/issues-overview';
import { KimOverview } from './components/kim-overview';
import { Summary } from './components/summary';
import { SummaryLoader } from './components/summary-loader';
import { SubsetStatCard } from '../../components/subset-stat-card';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import useGetSubsetOverview from '@/api/queries/useGetSubsetOverview';
import { useGetSubsetPersons } from '@/api/queries/useGetSubsetPersons';
import useGetSummary from '@/api/queries/useGetSummary';
import formatDate from '@/helpers/formatDate';
import { hasAnyOngoingRun } from '@/helpers/runs';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/skeleton';
import { SubTitle } from '@/new/components/subtitle';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { cn } from '@/new/lib/utils';
import { KimMode } from '@/types';

export function SubsetOverview() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const navigate = useNavigate();

  const { data: summaries, isLoading: summaryIsLoading } = useGetSummary(subsetId);
  const { data: overview, isLoading: overviewIsLoading } = useGetSubsetOverview(matterId, subsetId);
  const { data: matterData } = useGetMatterV2(matterId);
  const { data: subsetData } = useGetCase(subsetId);
  const { data: personsData } = useGetSubsetPersons(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter: matterData,
    subset: subsetData,
    currentPage: null, // This is the subset overview page
  });

  useEffect(() => {
    trackPageView(PageName.SubsetSummary);
  }, []);

  const handleAskQuestion = (question: string, mode: KimMode) => {
    if (!matterId || !question.trim()) return;
    navigate(`/matters/${matterId}/subsets/${subsetId}/kim`, { state: { question, mode } });
  };

  // Check if any run is ongoing (not just specific types)
  const isAnyRunOngoing = hasAnyOngoingRun(overview?.runs);

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <PageContainer>
        <PageHeader>
          <div className="flex items-center gap-6">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 px-0.5 text-sm text-gray-500">
                <Icon className="lucide--calendar" />
                <div>Created {formatDate(subsetData?.createdDate ?? '')}</div>
              </div>

              {subsetData ? (
                <PageTitle text={subsetData?.name ?? ''} iconClass="lucide--group" />
              ) : (
                <Skeleton className="mt-2 h-8 w-38" />
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button asChild variant="primary" disabled={isAnyRunOngoing}>
              <Link
                to={`/matters/${matterId}/subsets/${subsetId}/edit`}
                onClick={() => {
                  trackCustomEvent(TrackingEventsV2.EditExistingSubsetFromSummary, { subsetId });
                }}
                className={cn(isAnyRunOngoing && 'pointer-events-none opacity-50')}
              >
                <Icon className="lucide--group h-4 w-4" />
                Edit Subset Issues or Add Documents
              </Link>
            </Button>
          </div>
        </PageHeader>

        <div className="mb-6 flex flex-wrap items-stretch gap-3">
          <SubsetStatCard
            value={overview?.totalFacts?.toLocaleString()}
            to={`/matters/${matterId}/subsets/${subsetId}/facts`}
            onClick={() =>
              trackCustomEvent(TrackingEventsV2.ViewFactsFromSubsetSummary, {
                matterId,
                subsetId,
                isAllDocuments: subsetData?.isAllDocuments,
              })
            }
          >
            <Icon className="lucide--search text-gray-500" />
            <p>Facts</p>
          </SubsetStatCard>

          <SubsetStatCard
            value={overview?.disputedFacts?.toLocaleString()}
            to={`/matters/${matterId}/subsets/${subsetId}/inconsistencies`}
          >
            <Icon className="lucide--alert-triangle text-gray-500" />
            <p>Inconsistencies</p>
          </SubsetStatCard>

          <SubsetStatCard
            value={overview?.documents?.toLocaleString()}
            to={`/matters/${matterId}/subsets/${subsetId}/docs`}
            onClick={() =>
              trackCustomEvent(TrackingEventsV2.ViewDocumentsFromSubsetSummary, {
                matterId,
                subsetId,
                isAllDocuments: subsetData?.isAllDocuments,
              })
            }
          >
            <Icon className="lucide--file-text text-gray-500" />
            <p>Documents</p>
          </SubsetStatCard>

          <SubsetStatCard
            value={personsData?.length?.toLocaleString()}
            to={`/matters/${matterId}/subsets/${subsetId}/entities`}
            onClick={() =>
              trackCustomEvent(TrackingEventsV2.ViewEntitiesFromSubsetSummary, {
                matterId,
                subsetId,
                isAllDocuments: subsetData?.isAllDocuments,
              })
            }
          >
            <Icon className="lucide--users-round text-gray-500" />
            <p>Entities</p>
          </SubsetStatCard>
        </div>

        {summaryIsLoading ? (
          <SummaryLoader />
        ) : (
          <div className="space-y-6">
            {(() => {
              const hasSummary = Boolean(summaries?.content?.[0]?.content?.trim());
              const themes = summaries?.content?.slice(1) ?? [];
              const hasIssues = themes.length > 0 || summaryIsLoading;
              return (
                <>
                  <div>
                    {hasSummary && (
                      <Summary
                        summaryType="Subset"
                        summaryText={summaries?.content?.[0]?.content ?? ''}
                        isDeletedDocRunOngoing={isAnyRunOngoing}
                        isLoading={summaryIsLoading}
                        hasIssues={hasIssues}
                      />
                    )}
                    <IssuesOverview issues={themes} isLoading={summaryIsLoading} hasSummary={hasSummary} />
                  </div>

                  <div>
                    <Link to="kim" className="group mx-3 mb-4 flex w-fit items-center gap-3.5">
                      <SubTitle text="Ask Kim" iconClass="lucide--bot text-teal-500" />
                      <Icon className="lucide--arrow-right mt-0.25 text-gray-500 duration-150 group-hover:text-teal-500" />
                    </Link>
                    <KimOverview caseObject={subsetData} onAskQuestion={handleAskQuestion} />
                  </div>

                  <div className="mb-6 flex gap-4">
                    <KeyDocumentsSection overview={overview} overviewIsLoading={overviewIsLoading} />
                    <KeyEntitiesSection caseId={subsetId} />
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </PageContainer>
    </>
  );
}
